import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  getRetreatDetails,
  fieldValidationRules,
  getMandatoryFields,
  getFieldPlaceholder,
  getFieldLabel,
} from "../util/RetreatFieldsConfig";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";

const DynamicFormRenderer = ({
  category = "default",
  formData = {},
  onFormDataChange,
  onValidationChange,
  errors = {},
  readonly = false,
}) => {
  const [localErrors, setLocalErrors] = useState({});
  const [touched, setTouched] = useState({});

  // Get field configuration for the category
  const retreatDetails = getRetreatDetails(category) || {};
  const fieldConfig = retreatDetails.fields || {};
  const mandatoryFields = getMandatoryFields(category);

  // Validate a single field
  const validateField = (fieldName, value) => {
    const rules = fieldValidationRules[fieldName];
    const config = fieldConfig[fieldName];

    if (!rules) return null;

    // Check if field is required
    if (config?.mandatory && (!value || value === "")) {
      return `${getFieldLabel(fieldName)} is required`;
    }

    // Skip validation if field is empty and not required
    if (!value && !config?.mandatory) return null;

    // Apply validation rules
    if (rules.minLength && value.length < rules.minLength) {
      return rules.errorMessage || `Minimum length is ${rules.minLength}`;
    }

    if (rules.maxLength && value.length > rules.maxLength) {
      return rules.errorMessage || `Maximum length is ${rules.maxLength}`;
    }

    if (rules.pattern && !rules.pattern.test(value)) {
      return rules.errorMessage || "Invalid format";
    }

    if (rules.min && parseFloat(value) < rules.min) {
      return rules.errorMessage || `Minimum value is ${rules.min}`;
    }

    if (rules.max && parseFloat(value) > rules.max) {
      return rules.errorMessage || `Maximum value is ${rules.max}`;
    }

    if (rules.mustBeTrue && !value) {
      return rules.errorMessage || "This field must be checked";
    }

    return null;
  };

  // Validate all fields
  const validateAllFields = () => {
    const newErrors = {};

    Object.keys(fieldConfig).forEach((fieldName) => {
      const error = validateField(fieldName, formData[fieldName]);
      if (error) {
        newErrors[fieldName] = error;
      }
    });

    setLocalErrors(newErrors);

    // Notify parent component about validation status
    if (onValidationChange) {
      onValidationChange(newErrors);
    }

    return Object.keys(newErrors).length === 0;
  };

  // Handle field change
  const handleFieldChange = (fieldName, value) => {
    //console.log("Field changed:", fieldName, value);
    // Update form data
    const newFormData = { ...formData, [fieldName]: value };

    if (onFormDataChange) {
      onFormDataChange(newFormData);
    }

    // Mark field as touched
    setTouched((prev) => ({ ...prev, [fieldName]: true }));

    // Validate field if it has been touched
    if (touched[fieldName] || value) {
      const error = validateField(fieldName, value);
      setLocalErrors((prev) => ({
        ...prev,
        [fieldName]: error,
      }));
    }
  };

  // Handle field blur
  const handleFieldBlur = (fieldName) => {
    setTouched((prev) => ({ ...prev, [fieldName]: true }));

    const error = validateField(fieldName, formData[fieldName]);
    setLocalErrors((prev) => ({
      ...prev,
      [fieldName]: error,
    }));
  };

  // Get select options
  const getSelectOptions = (fieldName) => {
    const options = {
      sabbathGroup: [
        { value: "US", label: "United States" },
        { value: "CA", label: "Canada" },
        { value: "UK", label: "United Kingdom" },
        { value: "AU", label: "Australia" },
        { value: "IN", label: "India" },
        { value: "OTHER", label: "Other" },
      ],
      status: [
        { value: "New", label: "New" },
        { value: "Pending", label: "Pending" },
        { value: "Approved", label: "Approved" },
        { value: "Rejected", label: "Rejected" },
      ],
      type: [
        { value: "Retreat", label: "Retreat" },
        { value: "Conference", label: "Conference" },
        { value: "Workshop", label: "Workshop" },
      ],
    };

    return options[fieldName] || [];
  };

  // Render field based on type
  const renderField = (fieldName, config) => {
    const { type, icon } = config;
    const value = formData[fieldName] || "";
    const error = localErrors[fieldName] || errors[fieldName];
    const isMandatory = config.mandatory;
    const label = getFieldLabel(fieldName);

    // Set placeholder and style for optional fields
    const placeholder = getFieldPlaceholder(fieldName, isMandatory);

    // For input fields, pass style to input/textarea/select via style prop
    const readOnly = config.readOnly || readonly;

    const commonProps = {
      id: fieldName,
      name: fieldName,
      value,
      disabled: readonly,
      className: `form-control ${error ? "is-invalid" : ""}`,
      placeholder,
      onBlur: () => handleFieldBlur(fieldName),
    };

    const renderInputField = () => {
      switch (type) {
        case "email":
          return (
            <input
              {...commonProps}
              type="email"
              onChange={(e) => handleFieldChange(fieldName, e.target.value)}
            />
          );

        case "tel":
          if (fieldName === "phone" || fieldName === "emergencyContact") {
            return (

                <PhoneInput
                  international
                  flagUrl="https://flagcdn.com/w320/{xx}.png"
                  defaultCountry="US"
                  value={value}
                  onChange={(phone) => handleFieldChange(fieldName, phone)}
                  onBlur={() => handleFieldBlur(fieldName)}
                  disabled={readonly}
                  className={`form-control ${error ? "is-invalid" : ""}`}
                  inputProps={{
                    name: fieldName,
                    id: fieldName,
                    placeholder: placeholder,
                  }}
                  style={{
                    border: `1px solid ${error ? "#dc3545" : "#dad5ce"}`,
                    borderRadius: "8px",
                    
                    borderBottomLeftRadius: "0px",
                    borderTopLeftRadius: "0px",
                    overflow: "hidden",
                    flexGrow: 1,
                    backgroundColor: readonly ? "#e9ecef" : "white",
                  }}
                />

            );
          } else {
            return (
              <input
                {...commonProps}
                type="tel"
                onChange={(e) => handleFieldChange(fieldName, e.target.value)}
              />
            );
          }

        case "number":
          return (
            <input
              {...commonProps}
              type="number"
              min={fieldValidationRules[fieldName]?.min || 0}
              max={fieldValidationRules[fieldName]?.max || 999999}
              onChange={(e) => handleFieldChange(fieldName, e.target.value)}
            />
          );

        case "select":
          const options = getSelectOptions(fieldName);
          return (
            <select
              {...commonProps}
              onChange={(e) => handleFieldChange(fieldName, e.target.value)}
            >
              <option value="">Select {label}</option>
              {options.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          );

        case "checkbox":
          return (
            <div className="form-check">
              <input
                type="checkbox"
                id={fieldName}
                name={fieldName}
                checked={!!value}
                disabled={readonly}
                className={`form-check-input ${error ? "is-invalid" : ""}`}
                onChange={(e) => handleFieldChange(fieldName, e.target.checked)}
              />
              <label className="form-check-label" htmlFor={fieldName}>
                {fieldName === "consentToUseData" ? (
                  <>
                    {retreatDetails?.details?.consentText ||
                      "I consent to the use of my data for retreat purposes"}
                    {isMandatory && <span className="text-danger"> *</span>}
                  </>
                ) : fieldName === "parentConsent" ? (
                  <>
                    I have parental consent for participation
                    {isMandatory && <span className="text-danger"> *</span>}
                  </>
                ) : (
                  <>
                    {label}
                    {isMandatory && <span className="text-danger"> *</span>}
                  </>
                )}
                {placeholder && (
                  <span
                    className="ms-2"
                    data-bs-toggle="tooltip"
                    data-bs-placement="top"
                    title={placeholder}
                    style={{ cursor: "pointer" }}
                  >
                    <FontAwesomeIcon icon="info-circle" />
                  </span>
                )}
              </label>
            </div>
          );

        case "textarea":
          return (
            <textarea
              {...commonProps}
              rows={3}
              onChange={(e) => handleFieldChange(fieldName, e.target.value)}
            />
          );

        default:
          return (
            <input
              {...commonProps}
              type="text"
              onChange={(e) => handleFieldChange(fieldName, e.target.value)}
            />
          );
      }
    };

    // Don't render label for checkbox as it's handled inside the input
    if (type === "checkbox") {
      return (
        <div className="mb-3" key={fieldName}>
          {renderInputField()}
          {error && <div className="invalid-feedback d-block">{error}</div>}
        </div>
      );
    }

    return (
      <div className="mb-2 input-group " key={fieldName}>
        <span className="input-group-text" data-tip data-for={fieldName}>
          {icon && <FontAwesomeIcon icon={icon} className="me-2" />}
          {isMandatory && <span className="text-danger"> *</span>}
          {/* <label htmlFor={fieldName} className="form-label">
          {label}
          </label> */}
        </span>
        {readOnly ? (
          <span>
            {value !== "" && value !== undefined && value !== null ? (
              type === "checkbox" ? (
                value ? (
                  "Yes"
                ) : (
                  "No"
                )
              ) : type === "select" ? (
                getSelectOptions(fieldName).find((opt) => opt.value === value)
                  ?.label || value
              ) : (
                value
              )
            ) : (
              <span className="mt-4 text-muted">—</span>
            )}
          </span>
        ) : (
          renderInputField()
        )}
        {error && <div className="invalid-feedback d-block">{error}</div>}
      </div>
    );
  };

  // Group fields for better layout
  const groupFields = () => {
    const fieldNames = Object.keys(fieldConfig);

    /*const groups = {
      personal: ["firstName", "lastName", "email", "phone"],
      address: ["address", "city", "state", "zip", "country"],
      retreat: [
        "category",
        "sabbathGroup",
        "participantCount",
        "retreatDateString",
      ],
      special: [
        "instrument",
        "musicalExperience",
        "diocese",
        "ordinationYear",
        "emergencyContact",
      ],
      admin: ["type", "status", "remarks"],
      consent: ["consentToUseData", "parentConsent"],
    };*/
    const groups = {
      personal: [
        "firstName",
        "lastName",
        "email",
        "phone",
        "address",
        "city",
        "state",
        "zip",
        "country",
        "category",
        "sabbathGroup",
        "spouseEmail",
        "participantCount",
        "retreatDateString",
        "musicalExperience",
        "diocese",
        "ordinationYear",
        "emergencyContact",
        "consentToUseData",
        "parentConsent",
        "prayerIntentions",
      ],
      admin: ["type", "status", "remarks"],
    };

    const grouped = {
      personal: [],
      address: [],
      retreat: [],
      special: [],
      admin: [],
      consent: [],
      other: [],
    };

    fieldNames.forEach((fieldName) => {
      let placed = false;
      for (const [groupName, groupFields] of Object.entries(groups)) {
        if (groupFields.includes(fieldName)) {
          grouped[groupName].push(fieldName);
          placed = true;
          break;
        }
      }
      if (!placed) {
        grouped.other.push(fieldName);
      }
    });
    //console.log("Grouped fields:", grouped);
    return grouped;
  };

  const fieldGroups = groupFields();

  // Expose validation function
  useEffect(() => {
    if (onValidationChange) {
      onValidationChange(localErrors);
    }
  }, [localErrors, onValidationChange]);

  return (
    <div className="container-fluid">
      {/* Two-column layout for large screens, single column for small screens */}
      <div className="container-fluid">
        <div>
          {/* Left column: Personal + Retreat + Admin */}
          {fieldGroups.personal.length > 0 && (
            <>
              <div className="row">
                {fieldGroups.personal.map((fieldName) => (
                  <div className="col-12 col-md-6" key={fieldName}>
                    {fieldConfig[fieldName]?.display !== false &&
                      renderField(fieldName, fieldConfig[fieldName])}
                  </div>
                ))}
              </div>
            </>
          )}
          {fieldGroups.retreat.length > 0 && (
            <>
              <h5 className="form-section-title">Retreat Information</h5>
              {fieldGroups.retreat.map((fieldName) =>
                renderField(fieldName, fieldConfig[fieldName])
              )}
            </>
          )}
          {fieldGroups.admin.length > 0 && (
            <>
              <h5 className="form-section-title">Administrative</h5>
              {fieldGroups.admin.map((fieldName) =>
                renderField(fieldName, fieldConfig[fieldName])
              )}
            </>
          )}
        </div>
        <div className="col-12 col-md-6">
          {/* Right column: Address + Special + Consent + Other */}
          {fieldGroups.address.length > 0 && (
            <>
              <h5 className="form-section-title">Address Information</h5>
              {fieldGroups.address.map((fieldName) =>
                renderField(fieldName, fieldConfig[fieldName])
              )}
            </>
          )}
          {fieldGroups.special.length > 0 && (
            <>
              <h5 className="form-section-title">Additional Information</h5>
              {fieldGroups.special.map((fieldName) =>
                renderField(fieldName, fieldConfig[fieldName])
              )}
            </>
          )}
          {fieldGroups.consent.length > 0 && (
            <>
              <h5 className="form-section-title">Consent & Agreements</h5>
              {fieldGroups.consent.map((fieldName) =>
                renderField(fieldName, fieldConfig[fieldName])
              )}
            </>
          )}
          {fieldGroups.other.length > 0 && (
            <>
              <h5 className="form-section-title">Other</h5>
              {fieldGroups.other.map((fieldName) =>
                renderField(fieldName, fieldConfig[fieldName])
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// Validation hook for external use
export const useDynamicFormValidation = (category, formData) => {
  const [errors, setErrors] = useState({});

  const validateForm = () => {
    // Get the correct field configuration for the category
    const retreatDetails = getRetreatDetails(category) || {};
    const fieldConfig = retreatDetails.fields || {};
    const newErrors = {};

    Object.keys(fieldConfig).forEach((fieldName) => {
      const config = fieldConfig[fieldName];
      const rules = fieldValidationRules[fieldName];
      const value = formData[fieldName];

      if (config?.mandatory && (!value || value === "")) {
        newErrors[fieldName] = `${
          fieldName.charAt(0).toUpperCase() + fieldName.slice(1)
        } is required`;
        return;
      }

      if (rules && value) {
        if (rules.pattern && !rules.pattern.test(value)) {
          newErrors[fieldName] = rules.errorMessage || "Invalid format";
        }
        if (rules.minLength && value.length < rules.minLength) {
          newErrors[fieldName] =
            rules.errorMessage || `Minimum length is ${rules.minLength}`;
        }
        if (rules.maxLength && value.length > rules.maxLength) {
          newErrors[fieldName] =
            rules.errorMessage || `Maximum length is ${rules.maxLength}`;
        }
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  return { errors, validateForm, setErrors };
};

export default DynamicFormRenderer;
